import { useNodeListQuery } from "@/components/organisms/Node/Node.hooks";
import type { SummaryPart, SummaryTone } from "../utils/nodeSummary";
import { getReferenceValueOptionsByTypename } from "@/objects";

function groupSummaryLines(
  parts: SummaryPart[],
): { indent: number; parts: SummaryPart[] }[] {
  const lines: { indent: number; parts: SummaryPart[] }[] = [];
  let cur: { indent: number; parts: SummaryPart[] } | null = null;

  for (const p of parts) {
    if (p.breakBefore || cur === null) {
      cur = { indent: p.indent ?? 0, parts: [] };
      lines.push(cur);
    }
    cur.parts.push(p);
  }
  return lines;
}

/** Typography hierarchy (no pills): weight + neutral steps; single accent on variables. */
const SUMMARY_TONE_CLASS: Record<SummaryTone, string> = {
  object: "[font-weight:600] text-foreground",
  filter: "[font-weight:600] text-muted-foreground",
  sort: "font-medium text-muted-foreground",
  limit: "font-medium text-muted-foreground",
  schedule: "font-medium text-muted-foreground",
  value:
    '[font-weight:400] text-muted-foreground [font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation_Mono","Courier_New",monospace] text-[0.92em]',
  variable: "[font-weight:600] text-primary",
  expression:
    '[font-weight:400] text-muted-foreground [font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation_Mono","Courier_New",monospace] text-[0.92em]',
  prompt: "font-medium italic text-muted-foreground",
  operation: "font-medium text-muted-foreground",
};

export function SummaryText({ parts }: { parts: SummaryPart[] }) {
  const lines = groupSummaryLines(parts);

  return (
    <div className="flex flex-col items-stretch gap-px">
      {lines.map((line, lineIndex) => (
        <div
          key={lineIndex}
          className="[line-height:1.35] [word-break:break-word]"
          style={{ paddingLeft: `${line.indent}em` }}
        >
          {line.parts.map((part, index) => (
            <span
              key={`${part.text}-${lineIndex}-${index}`}
              data-summary-tone={part.tone}
              className={part.tone ? SUMMARY_TONE_CLASS[part.tone] : undefined}
            >
              {part.referenceLookup && part.referenceIdentifier ? (
                <LiveReferencePart
                  referenceLookup={part.referenceLookup}
                  identifier={part.referenceIdentifier}
                  fallback={part.text}
                />
              ) : part.referenceObjectType && part.referenceIdentifier ? (
                <LiveReferencePart
                  referenceLookup={getReferenceValueOptionsByTypename(
                    part.referenceObjectType,
                  )([])}
                  identifier={part.referenceIdentifier}
                  fallback={part.text}
                />
              ) : (
                part.text
              )}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Resolves a live-reference identifier embedded in a summary part to
 * its rendered label, by re-running the same query the picker uses
 * (``useNodeListQuery``) and dispatching to the caller's
 * ``onRenderOption``. Apollo's cache makes this free in practice once
 * the picker has been opened (or any other view of the same query has
 * loaded). Falls back to the raw identifier while the cache is cold.
 *
 * Lives as its own component so the hook can be called per-part —
 * each reference value gets its own query subscription.
 */
function LiveReferencePart({
  referenceLookup,
  identifier,
  fallback,
}: {
  referenceLookup: NonNullable<SummaryPart["referenceLookup"]>;
  identifier: string;
  fallback: string;
}) {
  const { options } = useNodeListQuery<unknown>(referenceLookup);
  const match = options.find(
    (o) => (o as { identifier?: string }).identifier === identifier,
  );
  if (!match) return <>{fallback}</>;
  const rendered = referenceLookup.onRenderOption?.(match, 0);
  return <>{rendered ?? fallback}</>;
}
